import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Server, CheckCircle, XCircle, Loader2, Trash2, Eye, EyeOff, Download,
  Users, Package, Clock, AlertTriangle, Plug, Crown
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SAPIntegration() {
  const [form, setForm] = useState({ app_server: '', system_number: '00', client: '100', username: '', password: '', language: 'EN' });
  const [showPass, setShowPass] = useState(false);
  const [status, setStatus] = useState('not_configured');
  const [message, setMessage] = useState('');
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('connection');

  const refresh = useCallback(() => {
    axios.get(`${API}/integrations/sap/status`).then(r => {
      setStatus(r.data.status || 'not_configured');
      if (r.data.app_server) setForm(f => ({ ...f, app_server: r.data.app_server, system_number: r.data.system_number || '00', client: r.data.client || '100', username: r.data.username || '', language: r.data.language || 'EN' }));
    });
    axios.get(`${API}/integrations/sap/customers`).then(r => setCustomers(r.data)).catch(() => {});
    axios.get(`${API}/integrations/sap/materials`).then(r => setMaterials(r.data)).catch(() => {});
    axios.get(`${API}/integrations/sap/import-history`).then(r => setHistory(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleTest = async () => {
    if (!form.app_server || !form.username || !form.password) { setMessage('Server, username, and password are required.'); return; }
    setTesting(true); setMessage('');
    try {
      const res = await axios.post(`${API}/integrations/sap/test-connection`, form);
      setStatus(res.data.status); setMessage(res.data.message);
    } catch (e) { setStatus('error'); setMessage(e.response?.data?.detail || 'Connection failed.'); }
    setTesting(false);
  };

  const handleImport = async (type) => {
    setImporting(type); setMessage('');
    try {
      const res = await axios.post(`${API}/integrations/sap/import-${type}`);
      setMessage(res.data.message); refresh();
    } catch (e) { setMessage(e.response?.data?.detail || `Import ${type} failed.`); }
    setImporting(null);
  };

  const handleDisconnect = async () => {
    await axios.delete(`${API}/integrations/sap`);
    setStatus('not_configured'); setForm({ app_server: '', system_number: '00', client: '100', username: '', password: '', language: 'EN' });
    setCustomers([]); setMaterials([]); setHistory([]); setMessage('');
  };

  const connected = status === 'connected';

  return (
    <div data-testid="sap-integration-page" className="space-y-6 max-w-5xl">
      <div className="cp-page-header">
        <div className="flex items-center gap-3">
          <h1 className="cp-page-title flex items-center gap-2">
            <Server className="w-6 h-6 text-accent" /> SAP Integration
          </h1>
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/10 text-amber-600">
            <Crown className="w-3 h-3" /> Enterprise
          </span>
        </div>
        {connected && (
          <Button variant="outline" onClick={handleDisconnect} className="text-destructive hover:text-destructive" data-testid="sap-disconnect-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Disconnect
          </Button>
        )}
      </div>

      {/* Status */}
      <Card className={`p-4 animate-fade-in ${connected ? 'border-green-500/30 bg-green-500/[0.03]' : ''}`} data-testid="sap-status-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {testing ? <Loader2 className="w-5 h-5 text-accent animate-spin" /> : connected ? <CheckCircle className="w-5 h-5 text-green-600" /> : status === 'error' ? <XCircle className="w-5 h-5 text-red-500" /> : <Plug className="w-5 h-5 text-muted-foreground" />}
            <div>
              <p className="text-sm font-medium">SAP Connection</p>
              <p className={`text-xs capitalize ${connected ? 'text-green-600' : status === 'error' ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="sap-status-text">
                {testing ? 'Testing...' : status === 'not_configured' ? 'Not Configured' : status}
              </p>
            </div>
          </div>
          <span className="cp-badge-indigo text-[10px]">n8n + OData</span>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4" data-testid="sap-tabs">
          <TabsTrigger value="connection" data-testid="tab-connection">Connection</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers ({customers.length})</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Materials ({materials.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        {/* ===== Connection Tab ===== */}
        <TabsContent value="connection" className="space-y-4 mt-4">
          <Card className="p-6 animate-fade-in" data-testid="sap-credentials-form">
            <h3 className="text-sm font-heading font-semibold mb-1">SAP Credentials</h3>
            <p className="text-xs text-muted-foreground mb-5">Enter your SAP system details. Credentials are encrypted before storage. BYOL: you maintain your own SAP license.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Application Server</Label>
                <Input value={form.app_server} onChange={e => setForm({ ...form, app_server: e.target.value })} placeholder="e.g. sap.company.com" data-testid="sap-server-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>System No.</Label><Input value={form.system_number} onChange={e => setForm({ ...form, system_number: e.target.value })} placeholder="00" data-testid="sap-sysnum-input" /></div>
                <div><Label>Client</Label><Input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} placeholder="100" data-testid="sap-client-input" /></div>
              </div>
              <div>
                <Label>Username</Label>
                <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="SAP user" data-testid="sap-username-input" />
              </div>
              <div>
                <Label>Password</Label>
                <div className="relative">
                  <Input type={showPass ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="SAP password" className="pr-10" data-testid="sap-password-input" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="sap-toggle-password">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Language</Label>
                <Input value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} placeholder="EN" data-testid="sap-language-input" />
              </div>
            </div>

            {message && (
              <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg text-sm ${connected ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`} data-testid="sap-message">
                {connected ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                {message}
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <Button onClick={handleTest} disabled={testing} className="bg-primary hover:bg-primary/90" data-testid="sap-test-btn">
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                Test Connection
              </Button>
              {connected && (
                <>
                  <Button variant="outline" onClick={() => handleImport('customers')} disabled={!!importing} data-testid="sap-import-customers-btn">
                    {importing === 'customers' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                    Import Customers
                  </Button>
                  <Button variant="outline" onClick={() => handleImport('materials')} disabled={!!importing} data-testid="sap-import-materials-btn">
                    {importing === 'materials' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                    Import Materials
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* BYOL Info */}
          <Card className="p-4 bg-muted/50 animate-fade-in" data-testid="sap-byol-info">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>Architecture:</strong> Veridex connects to SAP via n8n Community Edition (self-hosted) using OData Services.
              No SAP BTP or commercial middleware required. Your SAP system must have OData services enabled.
              Integration uses BAPI_CUSTOMER_GETLIST and BAPI_MATERIAL_GETLIST.
            </p>
          </Card>
        </TabsContent>

        {/* ===== Customers Tab ===== */}
        <TabsContent value="customers" className="mt-4">
          <div className="cp-table-wrapper animate-fade-in" data-testid="sap-customers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer No.</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Postal Code</TableHead>
                  <TableHead className="text-right">Imported</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    No customers imported. Connect to SAP and click "Import Customers".
                  </TableCell></TableRow>
                ) : customers.map((c, i) => (
                  <TableRow key={c.customer_number || i} data-testid={`sap-customer-row-${i}`}>
                    <TableCell className="font-mono text-xs">{c.customer_number}</TableCell>
                    <TableCell className="font-medium text-sm">{c.name}</TableCell>
                    <TableCell className="text-sm">{c.city}</TableCell>
                    <TableCell><span className="cp-badge-indigo text-xs">{c.country}</span></TableCell>
                    <TableCell className="font-mono text-xs">{c.postal_code}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{c.imported_at ? new Date(c.imported_at).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ===== Materials Tab ===== */}
        <TabsContent value="materials" className="mt-4">
          <div className="cp-table-wrapper animate-fade-in" data-testid="sap-materials-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material No.</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>UoM</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead className="text-right">Imported</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    No materials imported. Connect to SAP and click "Import Materials".
                  </TableCell></TableRow>
                ) : materials.map((m, i) => (
                  <TableRow key={m.material_number || i} data-testid={`sap-material-row-${i}`}>
                    <TableCell className="font-mono text-xs">{m.material_number}</TableCell>
                    <TableCell className="font-medium text-sm">{m.description}</TableCell>
                    <TableCell><span className="cp-badge-indigo text-xs">{m.material_group}</span></TableCell>
                    <TableCell className="font-mono text-xs">{m.base_uom}</TableCell>
                    <TableCell className="text-xs">{m.industry_sector}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{m.imported_at ? new Date(m.imported_at).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ===== History Tab ===== */}
        <TabsContent value="history" className="mt-4">
          <div className="cp-table-wrapper animate-fade-in" data-testid="sap-history-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead className="text-right">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                    <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" /> No import history yet
                  </TableCell></TableRow>
                ) : history.map((h, i) => (
                  <TableRow key={i} data-testid={`sap-history-row-${i}`}>
                    <TableCell className="capitalize font-medium text-sm flex items-center gap-2">
                      {h.type === 'customers' ? <Users className="w-3.5 h-3.5 text-accent" /> : <Package className="w-3.5 h-3.5 text-primary" />}
                      {h.type}
                    </TableCell>
                    <TableCell>
                      <span className={h.status === 'success' ? 'cp-badge-green' : 'cp-badge-red'}>
                        {h.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                        {h.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">{h.records}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
